import { UserErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';

import { Messages } from '../../../codegen/Localize';
import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '../../../commonInfra/contentActions/ActionMessages';
import { ContentActionNotification} from '../../../commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem, MultipleItemResult
} from '../../../commonInfra/contentActions/MultipleItemResult';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { INamedItemInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { NotificationHelper } from '../../components/helpers/NotificationHelper';
import { IUserInfo } from '../../users/models/IUserInfo';
import { LicenseTierFilter } from '../filters/LicenseTierFilter';
import { filter } from '../filters/SiteRoleFilter';
import { LicensingTier, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';


// Notification For:
// => site role change for single site

export class SiteRolesUpdateActionNotification {

  public static readonly instance = new SiteRolesUpdateActionNotification();

  public notify(
    result: IResult,
    users: Set<IUserInfo>,
    siteRoleEnum: string) {

    const siteRoleOptionsResource = SiteRoleOptionsResource.instance;
    const translatedSiteRole = filter(siteRoleEnum);

    const successfulName = NotificationHelper.getSuccessfulName(users, result);
    const failingName = NotificationHelper.getFailingName(users, result);

    // create a notification object for result reporting
    const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
      new MultipleItemResult(users, result),
      {
        describeSuccessSummary: (successCount: number) => {
          if (successCount === 1) {
            return Messages.updateSiteRoleSuccessSingle({
              USER_NAME: L10n.elideIfLong(successfulName),
              SITE_ROLE: translatedSiteRole
            });
          }
          return Messages.updateSiteRoleSuccessMultiple({
            SUCCESS_COUNT_LOC: L10n.localizedCount(successCount),
            SITE_ROLE: translatedSiteRole
          });
        },
        describeErrorSummary: (errorCount: number) => {
          if (errorCount === 1) {
            return Messages.updateSiteRoleErrorSingle({
              USER_NAME: L10n.elideIfLong(failingName),
              SITE_ROLE: translatedSiteRole
            });
          }
          return Messages.updateSiteRoleErrorMultiple({
            ERROR_COUNT_LOC: L10n.localizedCount(errorCount),
            SITE_ROLE: translatedSiteRole
          });
        },
        describeErrorDetail: (errorItem: IErrorItem<IUserInfo>) => {
          return {
            itemName: errorItem.item.username,
            itemUrl: NotificationHelper.getUrlForUser(<IUserInfo>errorItem.item),
            errorMessage: this.errorMessageFor(errorItem, siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRoleEnum).licenseTierRole, translatedSiteRole)
          };
        }
      }
    );

    // pass the notification object to the service for displaying
    ContentActionNotification.displayItemizedToastsForResult(notification);
  }

  private errorMessageFor(errorItem: IErrorItem<INamedItemInfo>, licenseTier: LicensingTier, translatedSiteRole: string) {
    const translatedLicenseRole = LicenseTierFilter.filter(licenseTier);
    switch (errorItem.error.code) {
      case UserErrorCode.UserCannotHaveSiteRoleLowerThanHighestMinimumLulGroupRole:
        return Messages.errorUserCannotHaveSiteRoleLowerThan_highestMinimumLulGroupRole({SITE_ROLE: translatedSiteRole});
      case UserErrorCode.UserInsufficientLicenses:
        return Messages.errorUserInsufficientLicensesInTier_updateSiteRole({LICENSE_TIER: translatedLicenseRole});
      case UserErrorCode.UserNotSupportedGuestOperation:
        return Messages.errorUserNotSupportedGuestOperation();
      case UserErrorCode.UserNotSupportedAdminOperation:
        return Messages.errorUserNotSupportedAdminOperation();
      case UserErrorCode.UserNotSupportedSelfOperation:
        return Messages.errorUserNotSupportedSelfOperation();
      case UserErrorCode.UserInsufficientSiteQuota:
        return Messages.errorUserInsufficientSiteQuota();
      case UserErrorCode.UserCannotChangeFromSupportUser:
        return Messages.errorUserCannotChangeFromSupportUser();
      default:
        return Messages.errorUnknown();
    }
  }
}

export default SiteRolesUpdateActionNotification.instance;
